import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { supabase, TABLES } from '../../supabase/supabase';
import SafeIcon from '../../common/SafeIcon';
import { FiCamera, FiTrash2, FiArrowRight, FiPieChart } from 'react-icons/fi';

const Snapshots = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from(TABLES.JOURNEY_SNAPSHOTS)
      .select('id, name, description, view_mode, filter_config, data, created_at')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });
    setSnapshots(data || []);
    setLoading(false);
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this snapshot? This cannot be undone.')) return;
    await supabase.from(TABLES.JOURNEY_SNAPSHOTS).delete().eq('id', id);
    setSnapshots(prev => prev.filter(s => s.id !== id));
  };

  const sessionCount = (s) => (s.data?.sessions?.length || 0);
  const lockedChips = (s) =>
    (s.filter_config || [])
      .filter(d => (d.selected || []).length > 0)
      .map(d => `${d.label}: ${(d.selected || []).map(v => {
        const opt = (d.values || []).find(o => o.value === v);
        return opt?.label || v;
      }).join(', ')}`);

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rutabaga-pink" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-black text-rutabaga-dark tracking-tight">Snapshots</h1>
          <p className="text-gray-400 text-sm mt-1">Saved, curated views of your journey map. Create one from the Analysis tab.</p>
        </div>
        <button
          onClick={() => navigate(`/projects/${projectId}/analysis`)}
          className="flex items-center gap-2 px-4 py-2 rounded-md bg-rutabaga-dark text-white text-[10px] font-black uppercase tracking-widest hover:bg-black transition-colors"
        >
          <SafeIcon icon={FiPieChart} /> Go to Analysis
        </button>
      </div>

      {snapshots.length === 0 ? (
        <div className="border-2 border-dashed border-gray-200 rounded-lg py-20 flex flex-col items-center justify-center text-center">
          <SafeIcon icon={FiCamera} className="text-3xl text-gray-300 mb-3" />
          <p className="font-black text-rutabaga-dark">No snapshots yet</p>
          <p className="text-gray-400 text-sm mt-1 max-w-sm">
            In the Analysis tab, filter and arrange a journey map the way you want it, then choose “Save as snapshot”.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {snapshots.map(s => (
            <div
              key={s.id}
              onClick={() => navigate(`/projects/${projectId}/snapshots/${s.id}`)}
              className="group bg-white border border-gray-200 rounded-lg p-5 cursor-pointer hover:border-rutabaga-pink hover:shadow-sm transition-all"
            >
              <div className="flex items-start justify-between">
                <h3 className="font-black text-rutabaga-dark text-lg leading-tight">{s.name}</h3>
                <button
                  onClick={(e) => remove(s.id, e)}
                  className="text-gray-300 hover:text-rutabaga-pink transition-colors p-1"
                  title="Delete snapshot"
                >
                  <SafeIcon icon={FiTrash2} />
                </button>
              </div>
              {s.description && (
                <p className="text-sm text-gray-500 mt-1 line-clamp-2">{s.description}</p>
              )}
              <div className="flex flex-wrap gap-1.5 mt-3">
                {lockedChips(s).slice(0, 4).map((chip, i) => (
                  <span key={i} className="text-[9px] font-black uppercase tracking-wide bg-rutabaga-pink/10 text-rutabaga-pink px-2 py-0.5 rounded-full">
                    {chip}
                  </span>
                ))}
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                <span className="text-[10px] font-bold text-gray-400">
                  {sessionCount(s)} session{sessionCount(s) !== 1 ? 's' : ''} · {format(new Date(s.created_at), 'MMM d, yyyy')}
                </span>
                <span className="text-[10px] font-black text-rutabaga-pink uppercase tracking-widest flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  Open <SafeIcon icon={FiArrowRight} />
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Snapshots;
